import flatten from 'lodash.flatten';
import values from 'lodash.values';
import get from 'lodash.get';
import { BURST, DIBELS8 } from '../../constants/applications';
import { getDaysDiff } from '../utils/assessmentWindowsUtils';
import { MEASURE_BY_MEASURE_SCORE, MEASURE_NAMES } from '../../constants/assessments';

export const READMINISTER_AFTER_DAYS = 30;
export const WITHIN_CURRENT_BURST = 'WITHIN_CURRENT_BURST';
export const WITHIN_ANY_BURST = 'WITHIN_ANY_BURST';

/**
 * Generates dict with results grouped by two criteria:
 *  Latest measure result for within current burst instruction by date.
 *  - to check measure completeness required by current semester burst
 *  Latest measure result applicable for all bursts.
 *  - to check preceding measure completeness
 *  We ignore periods and check only by date to perform calculation within all periods including PM
 * @param dibelsResults - all dibels results grouped by period
 * @param burstResults - all burst results grouped by period
 * @param measuresStatusList - list of objects with gated measure statuses by period
 * @param instructionCreateDate - Date object of current burst creation date
 * @returns Object with results create dates grouped bu measure and period.
 */
export const getLatestProbeResultDateByMeasureDict = (
    dibelsResults,
    burstResults,
    measuresStatusList,
    instructionCreateDate,
) => {
    return [
        ...flatten(values(dibelsResults)).filter(result => result.isValid),
        ...measuresStatusList,
        ...flatten(flatten(values(burstResults)).map(result => result.results)),
    ].reduce(
        (resultDateDict, result) => {
            const measure = result.measure;
            const resultCreateDate =
                measure === MEASURE_NAMES.MAZE ? result.modDate : result.clientModDate;
            const dateByMeasureDict = resultDateDict;
            const resultDate = new Date(resultCreateDate);
            if (
                resultDate >= instructionCreateDate &&
                (!dateByMeasureDict[WITHIN_CURRENT_BURST][measure] ||
                    dateByMeasureDict[WITHIN_CURRENT_BURST][measure] < resultDate)
            ) {
                dateByMeasureDict[WITHIN_CURRENT_BURST][measure] = resultDate;
            }
            if (
                !dateByMeasureDict[WITHIN_ANY_BURST][measure] ||
                dateByMeasureDict[WITHIN_ANY_BURST][measure] < resultDate
            ) {
                dateByMeasureDict[WITHIN_ANY_BURST][measure] = resultDate;
            }
            return dateByMeasureDict;
        },
        {
            [WITHIN_CURRENT_BURST]: {},
            [WITHIN_ANY_BURST]: {},
        },
    );
};

/**
 * Generate list of measures need to be completed by student within current semester burst.
 * List includes measures required by placement test status, current burst and group preceding measures
 * @param student - student object with statuses bu period
 * @param period - current period
 * @param instruction - burst instruction object
 * @param studentResults - object with all results for dibels and burst grouped by period
 * @returns List of measure names.
 */
export const getBurstRecommendation = (student, period, instruction, studentResults) => {
    const instructionCreateDate = new Date(instruction.instruction_create_date);
    const now = new Date(Date.now());
    const gatedMeasuresStatus = get(student.appStatuses.dibels8[period], 'gatedMeasuresStatus', {});
    const measuresStatusList = Object.keys(gatedMeasuresStatus).map(measure => ({
        ...gatedMeasuresStatus[measure],
        measure: MEASURE_BY_MEASURE_SCORE[measure] || measure,
    }));
    const latestProbeResultDateByMeasureDict = getLatestProbeResultDateByMeasureDict(
        studentResults[DIBELS8],
        studentResults[BURST],
        measuresStatusList,
        instructionCreateDate,
    );
    const requiredPlacementTestMeasures =
        get(student, `appStatuses.burst[${period}].requiredPlacementTestMeasures`, []) || [];
    const burstRecommendedMeasures = instruction.recommended_measures.map(assmtMeasure =>
        // Both CS Retell / CS Recall measure assessment start from CS Passage
        // TODO: handle / display CS Retell / CS Recall measures separately, not as a single Retell / Recall measure
        [MEASURE_NAMES.CS_RECALL, MEASURE_NAMES.CS_RETELL].includes(assmtMeasure.measure)
            ? MEASURE_NAMES.CS_PASSAGE
            : assmtMeasure.measure,
    );
    const precedingRecommendedMeasures = instruction.preceding_recommended_measures.map(
        assmtMeasure => assmtMeasure.measure,
    );
    return [
        ...requiredPlacementTestMeasures,
        ...burstRecommendedMeasures.filter(measure => {
            const latestProbeResultDate =
                latestProbeResultDateByMeasureDict[WITHIN_CURRENT_BURST][measure];
            return (
                !latestProbeResultDate ||
                getDaysDiff(latestProbeResultDate, now) >= READMINISTER_AFTER_DAYS
            );
        }),
        ...precedingRecommendedMeasures.filter(measure => {
            const latestProbeResultDate =
                latestProbeResultDateByMeasureDict[WITHIN_ANY_BURST][measure];
            return (
                !latestProbeResultDate ||
                getDaysDiff(latestProbeResultDate, now) >= READMINISTER_AFTER_DAYS
            );
        }),
    ];
};
